import { useMemo } from "react";
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement,
  LineElement, Filler, Tooltip, Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

// `orders` already arrives scoped to a single month (the batch_orders
// endpoint filters server-side by year/month), so this only needs to bucket
// by day-of-month — unlike the Dashboard's OrdersChart, which spans a whole
// year bucketed by month.
export function DailySalesChart({ orders, year, month, height = "260px" }) {
  const { labels, paidByDay, openByDay } = useMemo(() => {
    const daysInMonth = new Date(year, month, 0).getDate();
    const paid = new Array(daysInMonth).fill(0);
    const open = new Array(daysInMonth).fill(0);
    (orders || []).forEach(o => {
      const day = new Date(o.created_at).getDate();
      if (day < 1 || day > daysInMonth) return;
      if (o.paid) paid[day - 1] += parseFloat(o.amount) || 0;
      else open[day - 1] += parseFloat(o.amount) || 0;
    });
    return {
      labels: Array.from({ length: daysInMonth }, (_, i) => String(i + 1)),
      paidByDay: paid,
      openByDay: open,
    };
  }, [orders, year, month]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        position: "top",
        labels: { font: { size: 11 }, usePointStyle: true, pointStyleWidth: 8, padding: 16, color: "#718096" },
      },
      tooltip: {
        backgroundColor: "rgba(0,0,0,.82)",
        titleFont: { size: 12, weight: "600" },
        bodyFont: { size: 11 },
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          title: items => `Dia ${items[0].label}`,
          label: ctx => ` ${ctx.dataset.label}: ${parseFloat(ctx.raw).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}`,
        },
      },
    },
    scales: {
      x: { grid: { color: "rgba(0,0,0,.04)" }, ticks: { font: { size: 9 }, color: "#a0aec0", maxRotation: 0 } },
      y: {
        grid: { color: "rgba(0,0,0,.04)" },
        ticks: { font: { size: 10 }, color: "#a0aec0", callback: v => `R$${(v / 1000).toFixed(0)}k` },
        beginAtZero: true,
      },
    },
  };

  const data = {
    labels,
    datasets: [
      {
        label: "Pagos",
        data: paidByDay,
        borderColor: "#004a33",
        backgroundColor: "rgba(0,74,51,.1)",
        borderWidth: 2,
        tension: 0.3,
        fill: true,
        pointRadius: 2,
        pointHoverRadius: 5,
      },
      {
        label: "Em aberto",
        data: openByDay,
        borderColor: "#fc8f04",
        backgroundColor: "rgba(252,143,4,.1)",
        borderWidth: 2,
        tension: 0.3,
        fill: true,
        pointRadius: 2,
        pointHoverRadius: 5,
      },
    ],
  };

  return (
    <div style={{ height, width: "100%", position: "relative" }}>
      <Line options={options} data={data} />
    </div>
  );
}
